import { useAutoAnimateConditional } from '@/hooks/useAutoAnimateConditional';
import Button from './core/Button';
import { cn } from '@/helpers/classnames';
import { Spinner } from './core/Spinner';

interface SelectableListOption {
  id: string;
  label: string;
  description?: string;
}

interface SelectableListBaseProps {
  options: SelectableListOption[];
  noResultsMessage?: string;
  className?: string;
  isLoading?: boolean;
}

const SelectableListContainer = ({
  options,
  noResultsMessage,
  className,
  isLoading,
  children,
}: SelectableListBaseProps & { children: React.ReactNode }) => {
  const [listRef] = useAutoAnimateConditional<HTMLUListElement>();

  return (
    <ul
      ref={listRef}
      className={cn(
        'flex h-52 flex-col gap-1 overflow-y-auto rounded-md bg-background-100 p-4',
        className,
      )}
    >
      {children}
      {isLoading ? (
        <li className="m-auto list-none">
          <Spinner />
        </li>
      ) : options.length === 0 ? (
        <li>
          <i className="text-text-800">{noResultsMessage}</i>
        </li>
      ) : null}
    </ul>
  );
};

const SelectableListItem = ({
  option,
  isSelected,
  onClick,
  ...buttonProps
}: {
  option: SelectableListOption;
  isSelected: boolean;
  onClick: () => void;
} & Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, 'aria-pressed'>) => (
  <li>
    <Button
      className={cn(
        'h-fit w-full min-w-0 flex-1 flex-col items-start gap-0 overflow-hidden text-left font-normal',
        !isSelected && 'bg-background-50',
      )}
      onClick={onClick}
      variant={isSelected ? 'filled' : 'outline'}
      {...buttonProps}
    >
      <p>{option.label}</p>
      {option.description ? (
        <p className="w-full truncate text-left opacity-75">
          {option.description}
        </p>
      ) : null}
    </Button>
  </li>
);

export const SelectableList = ({
  selectedOption,
  setSelectedOption,
  ...listProps
}: SelectableListBaseProps & {
  selectedOption: string;
  setSelectedOption: (option: string) => void;
}) => (
  <SelectableListContainer {...listProps}>
    {listProps.options.map((option) => (
      <SelectableListItem
        key={option.id}
        option={option}
        isSelected={selectedOption === option.id}
        onClick={() => setSelectedOption(option.id)}
      />
    ))}
  </SelectableListContainer>
);

/** Several options can be selected. */
export const MultiSelectableList = ({
  selectedOptions,
  onSelectedOptionsChange,
  ...listProps
}: SelectableListBaseProps & {
  selectedOptions: string[];
  onSelectedOptionsChange: (options: string[]) => void;
}) => (
  <SelectableListContainer {...listProps}>
    {listProps.options.map((option) => {
      const isSelected = selectedOptions.includes(option.id);
      return (
        <SelectableListItem
          key={option.id}
          option={option}
          isSelected={isSelected}
          aria-pressed={isSelected}
          onClick={() =>
            onSelectedOptionsChange(
              isSelected
                ? selectedOptions.filter((id) => id !== option.id)
                : [...selectedOptions, option.id],
            )
          }
        />
      );
    })}
  </SelectableListContainer>
);
